import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { dataTableFootClass, dataTableHeadClass } from '../../components/DataTablePanel';
import {
  Button,
  Badge,
  InfoIcon,
  ListEmptyState,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  XIcon,
} from '../../components/ui';
import { t } from '../../i18n';
import { InvoiceStatus } from '../../types/invoice';
import { PaymentAllocationSummary } from '../../types/payment';
import { formatMoney } from '../../utils/format';

export function PaymentPaidInvoicesButton({
  allocations,
}: {
  allocations: PaymentAllocationSummary[];
}) {
  const [open, setOpen] = useState(false);
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null);
  const labels = t().payments;
  const numbers = allocations
    .map((row) => row.invoiceNo || `#${row.invoiceId}`)
    .filter(Boolean)
    .join(', ');
  const hoverLabel = numbers || labels.paidInvoicesEmpty;

  return (
    <>
      <button
        type="button"
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-ink-muted hover:bg-surface-muted hover:text-ink"
        aria-label={labels.paidInvoicesOpen}
        onMouseEnter={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          setTip({ x: rect.left + rect.width / 2, y: rect.top });
        }}
        onMouseLeave={() => setTip(null)}
        onClick={() => {
          setTip(null);
          setOpen(true);
        }}
      >
        <InfoIcon className="h-4 w-4" />
      </button>
      {tip
        ? createPortal(
            <span
              role="tooltip"
              className="pointer-events-none fixed z-[200] max-w-xs -translate-x-1/2 -translate-y-full rounded-md border border-line bg-surface-card px-2 py-1 text-xs text-ink shadow-md"
              style={{ left: tip.x, top: tip.y - 6 }}
            >
              {hoverLabel}
            </span>,
            document.body,
          )
        : null}
      <PaymentPaidInvoicesModal
        isOpen={open}
        allocations={allocations}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

function PaymentPaidInvoicesModal({
  isOpen,
  allocations,
  onClose,
}: {
  isOpen: boolean;
  allocations: PaymentAllocationSummary[];
  onClose: () => void;
}) {
  const labels = t().payments;
  const totals = allocations.reduce(
    (acc, row) => ({
      invoice: acc.invoice + Number(row.invoiceAmount),
      cash: acc.cash + Number(row.cashAmount),
      total: acc.total + Number(row.total),
    }),
    { invoice: 0, cash: 0, total: 0 },
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalHeader>
        <ModalTitle>{labels.paidInvoicesTitle}</ModalTitle>
      </ModalHeader>
      <ModalContent>
        {allocations.length === 0 ? (
          <ListEmptyState title={labels.paidInvoicesEmpty} />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-line">
            <table className="min-w-full text-sm">
              <thead className={dataTableHeadClass}>
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">
                    {labels.invoice}
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">
                    {t().invoices.status}
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    {t().financial.invoice}
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    {t().financial.cash}
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    {t().financial.grandTotal}
                  </th>
                </tr>
              </thead>
              <tbody>
                {allocations.map((row) => (
                  <tr
                    key={`${row.invoiceId}-${row.id ?? ''}`}
                    className="border-t border-line"
                  >
                    <td className="px-3 py-2">
                      <Link
                        to={`/invoices/${row.invoiceId}`}
                        className="font-medium text-brand hover:underline"
                      >
                        {row.invoiceNo || `#${row.invoiceId}`}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      {row.status ? (
                        <AllocationInvoiceStatusBadge status={row.status} />
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatMoney(row.invoiceAmount)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatMoney(row.cashAmount)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">
                      {formatMoney(row.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className={dataTableFootClass}>
                <tr>
                  <td className="px-3 py-2">{t().common.total}</td>
                  <td className="px-3 py-2" />
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(totals.invoice.toFixed(2))}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(totals.cash.toFixed(2))}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">
                    {formatMoney(totals.total.toFixed(2))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </ModalContent>
      <ModalFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          <XIcon className="h-4 w-4" />
          {t().common.close}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function AllocationInvoiceStatusBadge({ status }: { status: InvoiceStatus }) {
  const variant =
    status === 'PAID'
      ? 'success'
      : status === 'OVERDUE'
        ? 'danger'
        : status === 'PARTIALLY_PAID'
          ? 'warning'
          : 'secondary';

  return <Badge variant={variant}>{t().invoices.statuses[status]}</Badge>;
}
